/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectionManagementWizard/interfaces"/>
import { ExtensionsTypeLookup, ListItem, SortInfo } from "@medius/ui-controls";
import { ReasonCodeOperationDto } from "../shared/interfaces";

export enum RejectionType {
    Delete,
    Invalidate
}

export interface IReasonCode {
    id: number;
    reasonCode: string;
    description: string;
    systemReasonCode: string;
    operations: string[];
    types: string[];
    isSystemReasonCode: boolean;
    rejectionActionId? : number | null;
    rejectOperation: RejectOperation;
}

export enum ReasonCodeType {
    Internal = "Internal",
    External = "External"
}

export enum RejectOperation {
    CodingLine = "CodingLine",
    Deviation = "Deviation",
    InvoiceAmount = "InvoiceAmount"
}

export interface IRejectionAction {
    id: number;
    code: string;
    name: string;
    description: string;
}

export interface ISupplierInvoiceRejectionDto extends ReasonCodeOperationDto {
    rejectedEntityType: string;
    rejectionType: RejectionType;
    hasExternalReasonCode: boolean;
    sendToSupplier: boolean;
    invoiceRejectionEmail?: IInvoiceRejectionEmailDto;
}

export interface IInvoiceRejectionEmailDto {
    emailTo: IRecipientAddress[];
    carbonCopy: IRecipientAddress[];
    sendCopy: boolean;
    messageSubject: boolean;
    content: string;
}

export interface IRecipientAddress {
    value: string;
}

export interface IInvoiceRejectionReasonItem {
    reasonCodeId: number;
    actionId?: number | null;
}

export interface IInvoiceReasonCodeData {
    invoiceReasonCodes: IReasonCode[];
    companyConfiguration: RejectionManagementCompanyConfiguration;
    totalInvoiceReasonCodeCount: number;
    actions: ListItem[];
    isLoading: boolean;
    isError: boolean;
    paging: {
        currentPage: number;
        pageSize: number;
        changePage: (newPage: number) => void;
        changePageSize: (newPageSize: number) => void;
    };
    sorting: {
        sortInfo: SortInfo | null;
        changeSortInfo: (sortInfo: SortInfo | null) => void;
    };
    searching: {
        changeSearchTerm: (term: string) => void;
    };
}

export interface IEmailSettings { 
    currentUserEmail : string;
    currentUserLanguageCode : string;
    supplierLanguageCode : string;
}

export interface IEmailContent {
    subject: string;
    body: string;
}

export interface IEligibleForEmailCommunication {
    isEligible: boolean;
}

export type ConfigurationValue<T> =  {
    value?: T | null;
    sourceCompanyId?: number | null;
    sourceCompanyName?: string | null;
} | null;

export const CompanyConfigurationFields = {
    ReasonRequiredForDeletionAndInvalidation: "reasonRequiredForDeletionAndInvalidation",
    ReasonCodeRequiredUponDeviation: "reasonCodeRequiredUponDeviation",
    ReasonCodeRequiredUponInvoiceAmount: "reasonCodeRequiredUponInvoiceAmount",
    ReasonCodeRequiredUponCodingLines: "reasonCodeRequiredUponCodingLines",
    ReasonCodeRequiredForOnHoldOperation: "reasonCodeRequiredForOnHoldOperation",
    SupplierEmailForRejections: "supplierEmailForRejections"
} as const;

export type CompanyConfigurationField = typeof CompanyConfigurationFields[keyof typeof CompanyConfigurationFields];

export interface RejectionManagementCompanyConfiguration {
    companyId: number;
    reasonRequiredForDeletionAndInvalidation?: ConfigurationValue<boolean>;
    reasonCodeRequiredUponDeviation?: ConfigurationValue<boolean>;
    reasonCodeRequiredUponInvoiceAmount?: ConfigurationValue<boolean>;
    reasonCodeRequiredUponCodingLines?: ConfigurationValue<boolean>;
    reasonCodeRequiredForOnHoldOperation?: ConfigurationValue<boolean>;
    supplierEmailForRejections?: ConfigurationValue<SupplierEmailForRejections>;
    inheritedConfiguration?: RejectionManagementCompanyConfiguration;
}

export interface UpdateCompanyConfigurationPayload {
    reasonRequiredForDeletionAndInvalidation?: boolean | null;
    supplierEmailForRejections?: SupplierEmailForRejections | null;
    isReasonCodeRequiredUponDeviation: boolean | null;
    isReasonCodeRequiredUponInvoiceAmount: boolean | null;
    isReasonCodeRequiredUponCodingLines: boolean | null;
    isReasonCodeRequiredForOnHoldOperation: boolean | null;
}

export enum SupplierEmailForRejections
{
    suppliersContactPerson = "SuppliersContactPerson",
    sendersEmailAddress = "SendersEmailAddress"
}

export interface DefaultSupplierEmail {
    emailSenderAddress?: string;
    supplierContactPersons: string[];
}

export interface RejectionReasonSelectionDto {
    reasonCode: string;
    reasonCodeDescription: string;
    rejectionActionCode?: string | null;
    rejectionActionDescription?: string | null;
}

export interface RejectionEmailContentRequestDto {
    invoiceRejectionReasons: RejectionReasonSelectionDto[],
    languageCode: string
}

// the same as for uploading attachment on the invoice
export const RejectionEmailSupportedAttachmentExtensions = [
    ExtensionsTypeLookup.csv,
    ExtensionsTypeLookup.doc,
    ExtensionsTypeLookup.docx,
    ExtensionsTypeLookup.gif,
    ExtensionsTypeLookup.jpegAll,
    ExtensionsTypeLookup.json,
    ExtensionsTypeLookup.msg,
    ExtensionsTypeLookup.pdf,
    ExtensionsTypeLookup.png,
    ExtensionsTypeLookup.ppt,
    ExtensionsTypeLookup.pptx,
    ExtensionsTypeLookup.ods,
    ExtensionsTypeLookup.txt,
    ExtensionsTypeLookup.xls,
    ExtensionsTypeLookup.xlsx,
    ExtensionsTypeLookup.xml,
    ExtensionsTypeLookup.zip,
    "application/octet-stream",
    "application/oxps",
    "application/postscript",
    "application/rtf",
    "application/vnd.etsi.asic-e+zip",
    "application/vnd.ms-excel",
    "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "application/vnd.ms-powerpoint.presentation.macroEnabled.12",
    "application/vnd.ms-word.document.macroEnabled.12",
    "application/vnd.ms-xpsdocument",
    "application/vnd.oasis.opendocument.text",
    "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
    "application/x-7z-compressed",
    "application/xslt+xml",
    "image/bmp",
    "image/svg+xml",
    "image/tiff",
    "image/vnd.dxf",
    "message/rfc822",
    "text/html"
];